import { create } from 'zustand'
import type { AffiliateWallet, AffiliateTransaction, DownlineUser } from '../types'

interface AffiliateState {
  wallet: AffiliateWallet | null
  transactions: AffiliateTransaction[]
  downlines: DownlineUser[]
  referralCode: string | null
  referredCount: number
  isLoading: boolean
  error: string | null
  fetchWallet: () => Promise<void>
  fetchTransactions: () => Promise<void>
  fetchDownlines: () => Promise<void>
  fetchAdminDownlines: () => Promise<DownlineUser[]>
  submitWithdrawal: (data: {
    tokens: number
    bank_name: string
    account_name: string
    account_number: string
    whatsapp_number: string
  }) => Promise<void>
}

export const useAffiliateStore = create<AffiliateState>()((set, get) => ({
  wallet: null,
  transactions: [],
  downlines: [],
  referralCode: null,
  referredCount: 0,
  isLoading: false,
  error: null,

  fetchWallet: async () => {
    set({ isLoading: true, error: null })
    try {
      const { useAuthStore } = await import('../stores')
      const { user } = useAuthStore.getState()
      if (!user) return

      const { backendClient } = await import('../utils/backend')
      const { data: { session } } = await backendClient.auth.getSession()
      const token = session?.access_token
      if (!token) return

      const res = await fetch('/api/affiliate/wallet', {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) throw new Error('Failed to fetch wallet')
      const data = await res.json()
      set({ wallet: data.wallet, referralCode: data.referralCode, referredCount: data.referredCount })
    } catch (err: any) {
      set({ error: err.message })
    } finally {
      set({ isLoading: false })
    }
  },

fetchTransactions: async () => {
      try {
        const { useAuthStore } = await import('../stores')
        const { user } = useAuthStore.getState()
        if (!user) return

        const { backendClient } = await import('../utils/backend')
        const { data: { session } } = await backendClient.auth.getSession()
        const token = session?.access_token
        if (!token) return

        const res = await fetch('/api/affiliate/transactions', {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!res.ok) throw new Error('Failed to fetch transactions')
        const data = await res.json()
        set({ transactions: data.transactions || [] })
      } catch (err: any) {
        set({ error: err.message })
      }
    },

  fetchDownlines: async () => {
    try {
      const { useAuthStore } = await import('../stores')
      const { user } = useAuthStore.getState()
      if (!user) return

      const { backendClient } = await import('../utils/backend')
      const { data: { session } } = await backendClient.auth.getSession()
      const token = session?.access_token
      if (!token) return

      const res = await fetch('/api/affiliate/downlines', {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) throw new Error('Failed to fetch downlines')
      const data = await res.json()
      set({ downlines: data.downlines || [] })
    } catch (err: any) {
      set({ error: err.message })
    }
  },

  fetchAdminDownlines: async () => {
    try {
      const { useAuthStore } = await import('../stores')
      const { user } = useAuthStore.getState()
      if (!user || user.role !== 'admin') return

      const { backendClient } = await import('../utils/backend')
      const { data: { session } } = await backendClient.auth.getSession()
      const token = session?.access_token
      if (!token) return

      const res = await fetch('/api/affiliate/admin-downlines', {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) throw new Error('Failed to fetch admin downlines')
      const data = await res.json()
      return data.affiliates || []
    } catch (err: any) {
      set({ error: err.message })
      return []
    }
  },

  submitWithdrawal: async (withdrawalData) => {
    set({ error: null })
    try {
      const { useAuthStore } = await import('../stores')
      const { user } = useAuthStore.getState()
      if (!user) throw new Error('Not authenticated')

      const { backendClient } = await import('../utils/backend')
      const { data: { session } } = await backendClient.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('Not authenticated')

      const res = await fetch('/api/affiliate/withdrawal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(withdrawalData)
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || 'Failed to submit withdrawal')
      }
      await get().fetchWallet()
      await get().fetchTransactions()
    } catch (err: any) {
      set({ error: err.message })
      throw err
    }
  }
}))
